import React, { memo } from "react";
import { Box, Typography } from "@mui/material";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
// import ChartDataLabels from "chartjs-plugin-datalabels";
import { Line } from "react-chartjs-2";

ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
);

interface LineDataset {
  label: string;
  data: number[];
  borderColor?: string;
  backgroundColor?: string;
  tension?: number;
}

interface GenericLineChartProps {
  title: string;
  labels: string[];
  datasets: LineDataset[];
  height?: number | string;
  options?: any;
}

const GenericLineChart: React.FC<GenericLineChartProps> = ({
  title,
  labels,
  datasets,
  height = 300,
  options,
}) => {
  const hasData = datasets.some(
    (ds) => ds.data.length > 0 && ds.data.some((v) => v !== 0)
  );

  if (!hasData) {
    return (
      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: 2,
          p: 2,
          boxShadow: 1,
          textAlign: "center",
        }}
      >
        <Typography
          fontWeight={600}
          mb={1}
          sx={{ textDecoration: "underline", textAlign: "left" }}
        >
          {title}
        </Typography>
        <Typography color="textSecondary" sx={{ textAlign: "left" }}>
          No Records Found
        </Typography>
      </Box>
    );
  }

  const lineData = { labels, datasets };

  const lineOptions = options || {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" as const },
      tooltip: { enabled: true },
      datalabels: {
        align: "top" as const,
        anchor: "end" as const,
        color: "#333",
        font: { weight: "bold" as const, size: 12 },
        formatter: (value: number) => value,
      },
    },
    scales: {
      x: {
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        grid: { color: "#eee" },
      },
    },
    elements: {
      line: { tension: 0.4 }, // smooth line
      point: { radius: 5, hoverRadius: 7 },
    },
  };

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: 2,
        p: 2,
        boxShadow: 1,
      }}
    >
      <Typography fontWeight={600} mb={1} sx={{textDecoration:"underline"}}>
        {title}
      </Typography>
      <Box sx={{ height }}>
        <Line data={lineData} options={lineOptions} />
      </Box>
    </Box>
  );
};

export default memo(GenericLineChart);
